import { OptionSymbolParams } from "helpers";
import api, { ListResponse, gridModelsToQuery } from "../api";
import type { GridModel } from "../api";
import { CompositeReportJobStatus } from "constants/index";
import { MLegOrderParams } from "./interfaces";
import {
  ReportResponse,
  ReportParams,
  NewReportRequest,
  NewReportResponse,
  BucketReportRequest,
  BucketReportResponse,
  PDFReportRequest,
  ReportPdfResponse,
  OrionTransactionParams,
  OrionTransaction,
  AccountHistoryRecord,
  AccountHistoryQueryParams,
  CreateAccountHistoryPayload,
  OptionTypeEnum,
} from "../../helpers/types";

interface CommonModel {
  id: number;
  created_on: string;
  created_by: number | null;
  updated_on: string;
  updated_by: number | null;
}

export interface Alert extends CommonModel {
  type: string;
  payload: {
    body: string;
  };
  status: string;
}

export interface UserAlert extends CommonModel {
  seen: boolean;
  type: string;
  payload: any;
  status: string;
  symbol?: string;
  contract_type?: string;
  net_avg_price?: number;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  order_id?: number;
  time_sent?: string;
  legs?: Array<{
    action: string;
    strike_price: number;
    expiration_date: string;
  }>;
}

export interface Advisor extends CommonModel {
  name: string;
}

export interface Account {
  id: number;
  client: number;
  account_number: string;
  orion_account_id: number;
  client__name: string;
  client__representative: number;
  client__representative__name: string;
  custodian: number;
  custodian__name: string;
  reg_type: number;
  reg_type__name: string;
  as_of_date: string;
}

export interface AccountStrategy {
  id: number;
  account: number;
  strategy: number;
  available_quantity: number;
  composite: boolean;
  reporting: string | null;
  reinvest: boolean;
  keyword: boolean;
  underlying: string | null;
  management: string | null;
  advisor: number | null;
  notes: string | null;
  manually_managed: boolean;
  strategy__name: string;
  strategy__strategy_group: number | null;
  advisor__name: string | null;
  client__name: string;
  account__number: string;
  custodian__name: string;
}

export interface Asset extends CommonModel {
  as_of_date: string;
  symbol: string;
  name: string;
  last_price: string;
  // Annotated when the assets list is queried with held_by_account.
  held?: boolean;
}

export interface StrategyPosition extends Account {
  position_id: number;
  stock_qty: number;
  available_stock_qty: string;
  option_qty: string;
  overriden_option_qty: string | null;
  new_option_pos: string;
  on_order_sell: string | null;
  on_order_buy: string | null;
  executed_order_sell: string | null;
  executed_order_buy: string | null;
  custodian__comp_id: string | null;
}

export interface OverridenPosition extends CommonModel {
  account_id: number;
  asset_id: number;
  quantity: string | null;
  strategy_id: number | null;
}

export interface Client {
  id: number;
  name: string;
  orion_client_id: number;
  representative: number;
  representative__name: string;
  as_of_date: string;
}

export interface Custodian extends CommonModel {
  name: string;
  comp_id: string | null;
  omnibus_account: string | null;
  as_of_date: string;
}

export interface SettlementDate {
  id: number;
  date: string;
}

export interface Position {
  id: number;
  asset__symbol: string;
  asset__name: string;
  asset__asset_category__name: string;
  asset__asset_class__name: string;
  asset__product_type__name: string;
  asset__product_sub_type__name: string;
  created_on: string;
  created_by: string;
  updated_on: string;
  updated_by: string;
  is_active: boolean;
  as_of_date: string;
  quantity: number;
  available_quantity: number | null;
  price: number;
  market_value: string;
  weight: number;
  unrealized_gain_loss: string;
  purchase_date: string;
  cost_basis: number;
  included_in_billing: boolean;
  outside_id: number;
  account: number;
  asset: number;
  mgmt_model: number;
  mgmt_style: number;
  representative: number;
}

export interface PortfolioStats {
  as_of_date: string;
  total_market_value: number;
  total_unrealized_gain_loss: number;
}

export interface RegType extends CommonModel {
  name: string;
  as_of_date: string;
}

export interface Representative extends CommonModel {
  as_of_date: string;
  rep_id: number | null;
  name: string;
  firm: number | null;
}

export interface Strategy {
  id: number;
  name: string;
  strategy_group: number;
  asset: number;
  call_or_put: boolean;
  current_call: number | null;
  current_call__symbol: string | null;
  current_call__name: string | null;
  previous_call: number | null;
  previous_call__symbol: string | null;
  previous_call__name: string | null;
  hedge_ratio: string;
  target_delta: string;
  cost_basis?: string | null;
  current_call_price: string | null;
  total_stocks_qty?: number | null;
  orion_stock_last_price?: string | null;
  aum?: number | null;
  optionSymbolParams: OptionSymbolParams | null;
  option_qty?: string | null;
  executed_open_qty?: string | null;
  executed_close_qty?: string | null;
  last_call_close?: string | null;
  ex_date_last?: string | null;
  div_amt_last?: string | null;
  ex_date_next?: string | null;
  div_amt_next?: string | null;
  next_earnings_date?: string | null;
}

export interface StrategyGroup {
  id: number;
  name: string;
}

export interface Order {
  id?: number;
  account: number;
  asset: number;
  quantity: number;
  type: string;
  side?: string;
  status?: string;
}

export interface NewCall {
  as_of_date: string;
  name: string;
  symbol: string;
}

export interface NotIncludedAccount extends Account {
  stock_qty: string;
}

export interface FactSetOption {
  REQ_SYM: string;
  OPT_CALL_OR_PUT: string;
  EXPIRATION_DATE: number;
  STRIKE_PRICE: number;
  LAST_PRICE: number;
  ASK: number;
  BID: number;
  DAYS_TO_EXPIRATION: number;
}

export interface NonOpticPosition extends CommonModel {
  name: string;
  symbol: string;
  client_name: string;
  account_number: string;
  custodian_name: string;
  option_qty: number;
}

export interface OptionChainParams {
  stock_symbol: String;
  strike_range: Number;
  exp_start: Number;
  exp_end: Number;
}

export interface RemoveAccountFromStrategyParams {
  id: number;
  strategy_id?: number;
  group_id?: number;
}

export interface UnseenAlertCountParams {
  user_id: number;
  alert_type?: string;
}

export interface UnseenAlertCountResponse {
  trade_signal_count: number;
  position_mismatch_count: number;
}

export interface MarkAlertsSeenParams {
  ids?: number[];
  filters?: Record<string, any>;
}

export interface StockHVParams {
  symbol: string;
  vol_days: Number;
}

export interface StockHV {
  symbol: string;
  vol_days: string;
  historical_volatility: string | null;
}

export interface CompositeReportParams {
  composite_id: number;
  start_date: string;
  end_date: string;
  excluded_accounts: number[];
}

export interface CompositeReportPreviewParams extends CompositeReportParams {
  account_id: number;
  exclude_stocks?: string[];
  include_stocks?: string[];
}

export interface ReportOpenPosition {
  id: number;
  symbol: string;
  qty: number;
  price: number;
  created_on_date: number;
  close_price: number;
}

export interface CompositeReportItem {
  account_number: string;
  start_date: string;
  end_date: string;
  start_value: number;
  end_value: number;
  perfomance: number;
  portfolio_percent?: number;
  weighted_average?: number;
  dividend_income: number;
  strategy_performances: {
    symbol: string;
    performance: number;
    md_performance: number;
    start_value: number;
    end_value: number;
    cash_flows: PerformanceCashFlow[];
    cash_flow: number;
  }[];
}

export interface PerformanceCashFlow {
  id: number;
  created_on_date: string;
  side: string;
  amount: number;
  symbol: string;
}

export interface OrionDividendTransaction {
  ticker: string;
  transaction_date: string;
  transaction_type: string;
  unit_balance: number; // shares qty
  transaction_amount: number; // income from transaction
}

export interface CompositeReportPreview extends CompositeReportItem {
  shares_data: {
    symbol: string;
    data: {
      start: {
        symbol: string;
        price: number;
        date: string;
        amount: number;
        overriden: boolean;
      };
      end: {
        symbol: string;
        price: number;
        date: string;
        amount: number;
        overriden: boolean;
      };
    };
  }[];
  strategy_performances: {
    symbol: string;
    performance: number;
    md_performance: number;
    start_value: number;
    end_value: number;
    cash_flows: PerformanceCashFlow[];
    cash_flow: number;
  }[];
  shares_start_value: number;
  start_adjustment: number;
  shares_end_value: number;
  end_adjustment: number;
  cash_flow: number;
  transactions_info: {
    id: number;
    created_on_date: string;
    qty: number;
    price: number;
    side: string;
    change: number;
    symbol: string;
  }[];
  start_open_positions: ReportOpenPosition[];
  end_open_positions: ReportOpenPosition[];
  dividend_transactions: OrionDividendTransaction[];
}

export interface CompositeReportJob {
  key: string;
  status: CompositeReportJobStatus;
  result: CompositeReportItem[] | null;
  status_message: string | null;
}

export interface CallHistoryRecord {
  symbol: string;
  side: string;
  change: number;
  order_date: string;
}

export interface CallsHistoryParams {
  strategy_id: number;
  start_date: string;
  end_date: string;
}

export interface Composite {
  id: number;
  name: string;
  is_active: boolean;
  strategy_group: number;
  strategy: number;
  is_multi_stock: boolean;
  orion_asset_level_strategy_id: string;
}

export interface StrategyHistory {
  id: number;
  strategy: Strategy;
  strategy_group: number;
  created_on: string;
  deleted_on: string | null;
}

export interface StrategyHistoryResponse
  extends ListResponse<StrategyHistory> {}

export interface StrategyHistoryParams {
  start_date: string;
  end_date: string;
  strategy_group: number;
}

export interface StateInfo extends CommonModel {
  state_name: string | null;
  state_abbreviation: string | null;
  tax_rate: string | null;
}

export interface TaxRate extends CommonModel {
  state: number;
  account: number | null;
  client: number | null;
  state_name?: string;
  account_number?: string;
  client_name?: string;
}

export const apiWithTrading = api
  .enhanceEndpoints({
    addTagTypes: [
      "Acccounts",
      "AccountStrategies",
      "Strategies",
      "StrategyPositions",
      "Alerts",
      "Clients",
      "NotIncludedAccounts",
      "UserAlert",
      "CompositeReport",
      "Composites",
      "StrategyHistory",
      "StrategyGroups",
      "Assets",
      "Report",
      "AccountHistory",
      "StateInfo",
      "TaxRate",
      // Shared with features/trading/buckets.ts — history mutations must
      // refresh the bucket member has_history statuses too.
      "Buckets",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addAccountStrategy: builder.mutation<
        AccountStrategy,
        Partial<AccountStrategy>
      >({
        query: ({ ...post }) => ({
          url: `/trading/account_strategies/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: [
          "Strategies",
          "AccountStrategies",
          "StrategyPositions",
          "NotIncludedAccounts",
        ],
      }),
      removeAccountStrategy: builder.mutation({
        query: (id) => ({
          url: `/trading/account_strategies/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["AccountStrategies"],
      }),
      listAlerts: builder.query<ListResponse<Alert>, GridModel>({
        query: (grid) => ({
          url: `/trading/alerts/`,
          params: gridModelsToQuery(grid),
          method: "GET",
        }),
        providesTags: ["Alerts"],
      }),
      listUserAlerts: builder.query<ListResponse<UserAlert>, GridModel>({
        query: (grid) => ({
          url: `/trading/user_alerts/`,
          params: gridModelsToQuery(grid),
          method: "GET",
        }),
        providesTags: ["UserAlert"],
      }),
      getUserAlert: builder.query<UserAlert, number | undefined>({
        query: (id) => ({
          url: `/trading/user_alerts/${id}/`,
          method: "GET",
        }),
        providesTags: ["UserAlert"],
      }),
      markUserAlertsSeen: builder.mutation<
        { updated: number },
        MarkAlertsSeenParams
      >({
        query: (body) => ({
          url: `/trading/user_alerts/mark_seen/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["UserAlert"],
      }),
      updateUserAlert: builder.mutation<
        UserAlert,
        Partial<UserAlert> & Pick<UserAlert, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/user_alerts/${id}/`,
          body: patch,
          method: "PATCH",
        }),
        invalidatesTags: ["UserAlert"],
      }),
      getUnseenAlertCount: builder.query<
        UnseenAlertCountResponse,
        UnseenAlertCountParams
      >({
        query: (params) => ({
          url: `/trading/user_alerts/unseen_count/`,
          params,
          method: "GET",
        }),
        providesTags: ["UserAlert"],
      }),
      listAccountStrategies: builder.query<
        ListResponse<AccountStrategy>,
        GridModel
      >({
        query: (grid) => ({
          url: `/trading/account_strategies/`,
          params: gridModelsToQuery(grid),
          method: "GET",
        }),
        providesTags: ["AccountStrategies"],
      }),
      listClients: builder.query<ListResponse<Client>, GridModel | string>({
        query: (params) => ({
          url: `/trading/clients/`,
          params:
            typeof params === "string"
              ? { search: params }
              : gridModelsToQuery(params),
          method: "GET",
        }),
        providesTags: ["Clients"],
      }),
      getClient: builder.query<Client, number | undefined>({
        query: (id) => ({
          url: `/trading/clients/${id}/`,
          method: "GET",
        }),
      }),
      listAccounts: builder.query<ListResponse<Account>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/accounts/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["Acccounts"],
      }),
      listSettlementDates: builder.query<
        ListResponse<SettlementDate>,
        GridModel
      >({
        query: (gridModel) => ({
          url: `/trading/settlement_dates/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      listPositions: builder.query<ListResponse<Position>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/positions/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      listStrategyPositions: builder.query<
        ListResponse<StrategyPosition>,
        GridModel
      >({
        query: (gridModel) => ({
          url: `/trading/strategy_positions/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["StrategyPositions"],
      }),
      listStrategies: builder.query<ListResponse<Strategy>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/strategies/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["Strategies"],
      }),
      getStrategyById: builder.query<Strategy, number>({
        query: (id) => ({
          url: `/trading/strategies/${id}/`,
          method: "GET",
        }),
        providesTags: ["Strategies"],
      }),
      updateStrategy: builder.mutation<
        Strategy,
        Partial<Strategy> & Pick<Strategy, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/strategies/${id}/`,
          body: patch,
          method: "PATCH",
        }),
        invalidatesTags: ["Strategies"],
      }),
      listStrategyGroups: builder.query<ListResponse<StrategyGroup>, GridModel>(
        {
          query: (gridModel) => ({
            url: `/trading/strategy_groups/`,
            params: gridModelsToQuery(gridModel),
            method: "GET",
          }),
          providesTags: ["StrategyGroups"],
        },
      ),
      getStats: builder.query<ListResponse<PortfolioStats>, void>({
        query: () => ({
          url: `/trading/stats/`,
          method: "GET",
        }),
      }),
      updateAccountStrategy: builder.mutation<
        AccountStrategy,
        Partial<AccountStrategy> & Pick<AccountStrategy, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/account_strategies/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["StrategyPositions", "AccountStrategies"],
      }),
      updateAlert: builder.mutation<Alert, Partial<Alert> & Pick<Alert, "id">>({
        query: ({ id, ...patch }) => ({
          url: `/trading/alerts/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["Alerts"],
      }),
      newOrder: builder.mutation<Order, Order>({
        query: (order) => ({
          url: `/trading/orders/`,
          method: "POST",
          body: order,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      newOrdersBulk: builder.mutation<ListResponse<Order>, Order[]>({
        query: (orders) => ({
          url: `/trading/orders/bulk/`,
          method: "POST",
          body: orders,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      newMLegOrdersBulk: builder.mutation<void, MLegOrderParams[]>({
        query: (orders) => ({
          url: `/trading/mleg_orders/bulk/`,
          method: "POST",
          body: orders,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      processOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/orders/process/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      processMlegOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/mleg_orders/process/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      sendStagedOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/orders/send_staged/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      sendStagedMlegOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/mleg_orders/send_staged/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      deleteStagedOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/orders/delete_staged/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      deleteStagedMlegOrders: builder.mutation<void, { asset_id: number }>({
        query: (params) => ({
          url: `/trading/mleg_orders/delete_staged/`,
          method: "POST",
          body: params,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      listOrders: builder.query<ListResponse<Order>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/orders/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      listComposites: builder.query<ListResponse<Composite>, void>({
        query: () => ({
          url: `/trading/composites/`,
          method: "GET",
        }),
      }),
      listRepresentatives: builder.query<
        ListResponse<Representative>,
        GridModel
      >({
        query: (gridModel) => ({
          url: `/trading/representatives/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      addNewClient: builder.mutation<Client, Partial<Client>>({
        query: ({ ...post }) => ({
          url: `/trading/clients/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: ["Clients"],
      }),
      removeClient: builder.mutation({
        query: (id) => ({
          url: `/trading/clients/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["Clients"],
      }),
      updateClient: builder.mutation<
        Client,
        Partial<Client> & Pick<Client, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/clients/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["Clients"],
      }),
      listCustodians: builder.query<ListResponse<Custodian>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/custodians/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      listRegTypes: builder.query<ListResponse<RegType>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/reg_types/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      newAccount: builder.mutation<Account, Partial<Account>>({
        query: ({ ...post }) => ({
          url: `/trading/accounts/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: ["Acccounts"],
      }),
      removeAccount: builder.mutation({
        query: (id) => ({
          url: `/trading/accounts/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["Acccounts"],
      }),
      updateAccount: builder.mutation<
        Account,
        Partial<Account> & Pick<Account, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/accounts/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["Acccounts"],
      }),
      listAdvisors: builder.query<ListResponse<Advisor>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/advisors/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      getAssetById: builder.query<Asset, Pick<Asset, "id">>({
        query: ({ id }) => ({
          url: `/trading/assets/${id}/`,
          method: "GET",
        }),
      }),
      newCall: builder.mutation<
        Strategy,
        Partial<NewCall & Pick<Strategy, "id">>
      >({
        query: ({ id, ...post }) => ({
          url: `/trading/strategies/${id}/new_call/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: ["Strategies", "StrategyPositions"],
      }),
      closeCall: builder.mutation<Strategy, Pick<Strategy, "id">>({
        query: ({ id }) => ({
          url: `/trading/strategies/${id}/close_call/`,
          method: "POST",
        }),
        invalidatesTags: ["Strategies", "StrategyPositions"],
      }),
      listNotIncludedAccounts: builder.query<
        ListResponse<NotIncludedAccount>,
        GridModel & Pick<Strategy, "id">
      >({
        query: ({ id, ...gridModel }) => ({
          url: `/trading/strategies/${id}/not_included_accounts/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["NotIncludedAccounts"],
      }),
      listOptionChain: builder.query<FactSetOption[], OptionChainParams>({
        query: (params) => ({
          url: `/trading/option_chain/`,
          body: params,
          method: "POST",
        }),
      }),
      fillStrategyStockWithAccounts: builder.mutation<
        AccountStrategy[],
        Pick<Strategy, "id">
      >({
        query: ({ id }) => ({
          url: `/trading/strategies/${id}/fill_with_accounts/`,
          method: "POST",
        }),
        invalidatesTags: ["StrategyPositions", "NotIncludedAccounts"],
      }),
      removeAccountFromStrategy: builder.mutation<
        void,
        RemoveAccountFromStrategyParams
      >({
        query: ({ id, ...post }) => ({
          url: `/trading/accounts/${id}/remove_from_strategy/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: [
          "StrategyPositions",
          "NotIncludedAccounts",
          "Strategies",
          "AccountStrategies",
          "AccountHistory",
        ],
      }),
      completelyDeleteAccountStrategy: builder.mutation<
        void,
        RemoveAccountFromStrategyParams
      >({
        query: ({ id, ...post }) => ({
          url: `/trading/accounts/${id}/remove_account_strategy/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: [
          "StrategyPositions",
          "NotIncludedAccounts",
          "Strategies",
          "AccountStrategies",
          "AccountHistory",
        ],
      }),
      listNonOpticPositions: builder.query<
        ListResponse<NonOpticPosition>,
        GridModel
      >({
        query: (gridModel) => ({
          url: `/trading/non_optic_positions/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
      }),
      overridePositionQuery: builder.mutation<
        OverridenPosition | {},
        Partial<OverridenPosition>
      >({
        query: ({ ...post }) => ({
          url: `/trading/positions/override/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: ["StrategyPositions"],
      }),
      getStockHistoricalVolatility: builder.query<StockHV, StockHVParams>({
        query: ({ symbol, vol_days }) => ({
          url: `/trading/historical_volatility/?symbol=${symbol}&vol_days=${vol_days}`,
          method: "GET",
        }),
      }),
      getCompositeReportPreview: builder.query<
        CompositeReportPreview,
        CompositeReportPreviewParams
      >({
        query: (params) => {
          const { exclude_stocks, include_stocks, ...rest } = params;
          const searchParams = new URLSearchParams();

          // Add all non-array parameters
          Object.entries(rest).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              searchParams.append(key, value.toString());
            }
          });

          // Add array parameters separately
          if (exclude_stocks) {
            exclude_stocks.forEach((value) => {
              searchParams.append("exclude_stocks", value.toString());
            });
          }

          if (include_stocks) {
            include_stocks.forEach((value) => {
              searchParams.append("include_stocks", value.toString());
            });
          }

          return {
            url: `/trading/composite_report_preview/?${searchParams.toString()}`,
            method: "GET",
          };
        },
      }),
      startCompositeReportJob: builder.mutation<
        CompositeReportJob,
        CompositeReportParams
      >({
        query: ({ ...post }) => ({
          url: `/trading/composite_report/start_job/`,
          method: "POST",
          body: post,
        }),
        invalidatesTags: ["CompositeReport"],
      }),
      getCompositeReport: builder.query<CompositeReportJob, { key: string }>({
        query: ({ key }) => ({
          url: `/trading/composite_report/?key=${key}`,
          method: "GET",
        }),
        providesTags: ["CompositeReport"],
      }),
      getCallsHistory: builder.query<CallHistoryRecord[], CallsHistoryParams>({
        query: (params) => ({
          url: `/trading/calls_history/?${new URLSearchParams(params as any).toString()}`,
          method: "GET",
        }),
      }),
      getStrategyHistory: builder.query<
        StrategyHistoryResponse,
        {
          start_date?: string;
          end_date?: string;
          strategy_group: number | number[];
          search?: string;
          page_size?: number;
          page?: number;
        }
      >({
        query: (params) => ({
          url: `/trading/strategy_history/`,
          params,
          method: "GET",
        }),
        providesTags: ["StrategyHistory"],
      }),
      getAccountHistory: builder.query<
        AccountHistoryRecord[],
        AccountHistoryQueryParams
      >({
        query: (params) => ({
          url: `/trading/account_history/`,
          params,
          method: "GET",
        }),
        providesTags: ["AccountHistory"],
      }),
      createAccountHistory: builder.mutation<
        AccountHistoryRecord,
        CreateAccountHistoryPayload
      >({
        query: (body) => ({
          url: `/trading/account_history/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["AccountHistory", "Buckets"],
      }),
      deleteAccountHistory: builder.mutation<
        void,
        Pick<AccountHistoryRecord, "id">
      >({
        query: ({ id }) => ({
          url: `/trading/account_history/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["AccountHistory", "Buckets"],
      }),
      updateAccountHistory: builder.mutation<
        AccountHistoryRecord,
        Partial<AccountHistoryRecord> & Pick<AccountHistoryRecord, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/account_history/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["AccountHistory", "StrategyPositions", "Buckets"],
      }),
      generateMaxTradeAmount: builder.mutation<
        AccountHistoryRecord[],
        { account_id: number; asset_id: number; option_type: OptionTypeEnum }
      >({
        query: (body) => ({
          url: `/trading/account_history/max_trade_amount/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["AccountHistory", "Buckets"],
      }),
      generateAccountHistoryFromOrion: builder.mutation<
        AccountHistoryRecord[],
        { account_id: number; asset_id: number; start_date?: string }
      >({
        query: (body) => ({
          url: `/trading/account_history/generate_from_orion/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["AccountHistory", "Buckets"],
      }),
      ensureAccountHistoryForReport: builder.mutation<
        { status: string; data: AccountHistoryRecord[] },
        { orion_account_id: number; asset_id: number; option_type: OptionTypeEnum }
      >({
        query: (body) => ({
          url: `/trading/account_history/ensure_for_report/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["AccountHistory", "Buckets"],
      }),
      getRemovedStrategies: builder.query<
        ListResponse<Strategy>,
        number | undefined
      >({
        query: (account_id) => ({
          url: `/trading/accounts/${account_id}/removed_strategies/`,
          method: "GET",
        }),
        providesTags: ["Strategies"],
      }),
      createStrategy: builder.mutation<Strategy, Partial<Strategy>>({
        query: (strategy) => ({
          url: `/trading/strategies/create_strategy/`,
          method: "POST",
          body: strategy,
        }),
        invalidatesTags: ["Strategies"],
      }),
      listAssets: builder.query<
        ListResponse<Asset>,
        {
          search?: string;
          page_size?: number;
          held_by_account?: number;
          held_only?: boolean;
          ordering?: string;
        }
      >({
        query: (params) => ({
          url: `/trading/assets/`,
          params,
          method: "GET",
        }),
        providesTags: ["Assets"],
      }),
      updateStrategyGroup: builder.mutation<
        Strategy,
        { id: number; strategy_group: number | null }
      >({
        query: ({ id, strategy_group }) => ({
          url: `/trading/strategies/${id}/update_strategy_group/`,
          method: "PUT",
          body: { strategy_group },
        }),
        invalidatesTags: ["Strategies"],
      }),
      listStateInfo: builder.query<ListResponse<StateInfo>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/state_info/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["StateInfo"],
      }),
      updateStateInfo: builder.mutation<
        StateInfo,
        Partial<StateInfo> & Pick<StateInfo, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/state_info/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["StateInfo"],
      }),
      createTaxRate: builder.mutation<TaxRate, Partial<TaxRate>>({
        query: (body) => ({
          url: `/trading/tax_rate/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["TaxRate"],
      }),
      listTaxRates: builder.query<ListResponse<TaxRate>, GridModel>({
        query: (gridModel) => ({
          url: `/trading/tax_rate/`,
          params: gridModelsToQuery(gridModel),
          method: "GET",
        }),
        providesTags: ["TaxRate"],
      }),
      updateTaxRate: builder.mutation<
        TaxRate,
        Partial<TaxRate> & Pick<TaxRate, "id">
      >({
        query: ({ id, ...patch }) => ({
          url: `/trading/tax_rate/${id}/`,
          method: "PATCH",
          body: patch,
        }),
        invalidatesTags: ["TaxRate"],
      }),
      deleteTaxRate: builder.mutation<void, Pick<TaxRate, "id">>({
        query: ({ id }) => ({
          url: `/trading/tax_rate/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["TaxRate"],
      }),
      getReport: builder.query<ReportResponse, ReportParams>({
        query: (params) => ({
          url: `/trading/report/`,
          method: "POST",
          body: params,
        }),
        providesTags: ["Report"],
      }),
      getNewReport: builder.mutation<NewReportResponse, NewReportRequest>({
        query: (body) => ({
          url: `/trading/new_report/`,
          method: "POST",
          body,
        }),
      }),
      // STRATEGY report over the account's Bucket 1/2: one excel with a
      // sheet per (asset, letter) pair.
      getBucketReport: builder.mutation<BucketReportResponse, BucketReportRequest>({
        query: (body) => ({
          url: `/trading/new_report/bucket/`,
          method: "POST",
          body,
        }),
      }),
      getReportPdf: builder.mutation<ReportPdfResponse, PDFReportRequest>({
        query: (body) => ({
          url: `/trading/report_pdf/`,
          method: "POST",
          body,
          responseHandler: (response) => response.json(),
        }),
      }),
      getOrionTransactions: builder.query<
        ListResponse<OrionTransaction>,
        OrionTransactionParams
      >({
        query: (params) => ({
          url: `/trading/orion_transactions/`,
          params,
          method: "GET",
        }),
      }),
    }),
  });

export const {
  useAddAccountStrategyMutation,
  useRemoveAccountStrategyMutation,
  useListClientsQuery,
  useListAccountsQuery,
  useListAccountStrategiesQuery,
  useListAlertsQuery,
  useGetClientQuery,
  useListSettlementDatesQuery,
  useListPositionsQuery,
  useListStrategyPositionsQuery,
  useListStrategiesQuery,
  useGetStrategyByIdQuery,
  useListStrategyGroupsQuery,
  useGetStatsQuery,
  useUpdateAccountStrategyMutation,
  useUpdateAlertMutation,
  useNewOrderMutation,
  useNewOrdersBulkMutation,
  useNewMLegOrdersBulkMutation,
  useListOrdersQuery,
  useListCompositesQuery,
  useListRepresentativesQuery,
  useAddNewClientMutation,
  useRemoveClientMutation,
  useUpdateClientMutation,
  useProcessOrdersMutation,
  useProcessMlegOrdersMutation,
  useListCustodiansQuery,
  useListRegTypesQuery,
  useNewAccountMutation,
  useRemoveAccountMutation,
  useUpdateAccountMutation,
  useListAdvisorsQuery,
  useSendStagedOrdersMutation,
  useSendStagedMlegOrdersMutation,
  useNewCallMutation,
  useCloseCallMutation,
  useListNotIncludedAccountsQuery,
  useDeleteStagedOrdersMutation,
  useDeleteStagedMlegOrdersMutation,
  useListOptionChainQuery,
  useGetAssetByIdQuery,
  useFillStrategyStockWithAccountsMutation,
  useRemoveAccountFromStrategyMutation,
  useCompletelyDeleteAccountStrategyMutation,
  useUpdateStrategyMutation,
  useListNonOpticPositionsQuery,
  useOverridePositionQueryMutation,
  useListUserAlertsQuery,
  useGetUserAlertQuery,
  useGetUnseenAlertCountQuery,
  useUpdateUserAlertMutation,
  useMarkUserAlertsSeenMutation,
  useGetStockHistoricalVolatilityQuery,
  useGetCompositeReportPreviewQuery,
  useStartCompositeReportJobMutation,
  useGetCompositeReportQuery,
  useGetCallsHistoryQuery,
  useGetStrategyHistoryQuery,
  useGetAccountHistoryQuery,
  useGenerateMaxTradeAmountMutation,
  useCreateAccountHistoryMutation,
  useDeleteAccountHistoryMutation,
  useCreateStrategyMutation,
  useListAssetsQuery,
  useUpdateStrategyGroupMutation,
  useListStateInfoQuery,
  useUpdateStateInfoMutation,
  useCreateTaxRateMutation,
  useListTaxRatesQuery,
  useUpdateTaxRateMutation,
  useDeleteTaxRateMutation,
  useGetReportQuery,
  useGetNewReportMutation,
  useGetBucketReportMutation,
  useGetReportPdfMutation,
  useGetOrionTransactionsQuery,
  useUpdateAccountHistoryMutation,
  useGetRemovedStrategiesQuery,
  useGenerateAccountHistoryFromOrionMutation,
  useEnsureAccountHistoryForReportMutation,
} = apiWithTrading;
